import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PiggyBank, Loader2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ProfitSweep = () => {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/platform-financials/profit-account`, auth())
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load profit account'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sweep = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents < 100) return toast.error('Enter an amount of at least $1.00');
    if (!window.confirm(`Move ${usd(cents)} from the Eventism Treasury into your Eventism Profits account? This moves real money.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/platform-financials/profit-sweep`, { amount_cents: cents }, auth());
      toast.success(`Sweep sent — ${usd(r.data.amount_cents)} → ${r.data.destination} (${r.data.status})`);
      setAmount('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-emerald-500/30" data-testid="profit-sweep-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold flex items-center gap-1.5"><PiggyBank className="h-4 w-4 text-emerald-400" /> Eventism Profits account <span className="text-[10px] text-muted-foreground font-normal">Stripe financial account ••••6123</span></p>
          {data?.found && (
            <p className="text-sm font-extrabold text-emerald-400" data-testid="profit-account-balance">{usd(data.account.available_cents)} <span className="text-[10px] font-normal text-muted-foreground">in profits</span></p>
          )}
        </div>

        {!data ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : !data.configured ? (
          <p className="text-xs text-muted-foreground" data-testid="profit-not-configured">
            To link your new <strong>Eventism Profits</strong> account, Stripe needs a restricted API key with Money Management permissions
            (Dashboard → Developers → API keys → Create restricted key → enable <em>Money Management: Financial Accounts (Read)</em>).
            Add it here as <code>STRIPE_MONEY_MGMT_KEY</code> and this card activates automatically.
          </p>
        ) : !data.found ? (
          <p className="text-xs text-muted-foreground">Key accepted, but no "Eventism Profits" financial account was found on your Stripe account.</p>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" inputMode="decimal"
                className="h-9 w-32 pl-6 text-sm" data-testid="sweep-amount-input" />
            </div>
            <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={sweep} disabled={sending} data-testid="sweep-submit-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sweep to Profits <ArrowRight className="h-3.5 w-3.5 ml-1" /></>}
            </Button>
            <p className="text-[10px] text-muted-foreground">Moves settled funds from the Treasury into {data.account.nickname}. Takes effect in Stripe immediately.</p>
          </div>
        )}

        {data?.sweeps?.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="sweep-history">
            {data.sweeps.map(s => (
              <span key={s.payout_id} className="text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">
                {usd(s.amount_cents)} · {s.status} · {new Date(s.created_at).toLocaleDateString()}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
